import { getGPT } from 'lib/ads';
import { useDidMount } from 'lib/hooks/useDidMount';
import { useCallback, useEffect } from 'react';
import { AD_TIMEOUT } from '../constants';

interface UseAdManagementOptions {
  showAdUnit: boolean;
  isAdBlocked: boolean;
  isAdFilled: boolean;
  isAdLoading: boolean;
  setIsAdBlocked: (blocked: boolean) => void;
  setIsAdFilled: (filled: boolean) => void;
  setIsAdLoading: (loading: boolean) => void;
}

export function useAdManagement({
  showAdUnit,
  isAdBlocked,
  isAdFilled,
  isAdLoading,
  setIsAdBlocked,
  setIsAdFilled,
  setIsAdLoading
}: UseAdManagementOptions) {
  const hasMounted = useDidMount();

  // Detect ad blockers by testing GPT loading
  useEffect(() => {
    if (!showAdUnit || !hasMounted) return;

    setIsAdLoading(true);

    // Test if GPT can load to detect ad blockers
    getGPT(AD_TIMEOUT)
      .then(() => {
        setIsAdBlocked(false);
        // GPT loaded, but we still need to wait for ad fill status
      })
      .catch((error: Error) => {
        if (
          error.message.includes(
            'GPT did not load within the specified timeout'
          )
        ) {
          setIsAdBlocked(true);
          setIsAdLoading(false);
        }
      });
  }, [showAdUnit, hasMounted, setIsAdBlocked, setIsAdLoading]);

  const handleAdFillChange = useCallback(
    (isFilled: boolean) => {
      setIsAdFilled(isFilled);
      // If we get a fill status (true or false), we're no longer loading
      setIsAdLoading(false);
    },
    [setIsAdFilled, setIsAdLoading]
  );

  // Add timeout for ad loading to handle cases where fill status is never reported
  useEffect(() => {
    if (!isAdLoading || isAdBlocked) return;

    const timeout = setTimeout(() => {
      // If ad is still loading after 5 seconds, assume it failed
      if (isAdLoading && !isAdFilled) {
        setIsAdLoading(false);
        setIsAdFilled(false);
      }
    }, AD_TIMEOUT);

    return () => clearTimeout(timeout);
  }, [isAdLoading, isAdFilled, isAdBlocked, setIsAdLoading, setIsAdFilled]);

  return {
    handleAdFillChange
  };
}
